
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import { IdState } from 'vue-virtual-scroller';
import { namespace } from 'vuex-class';

import BackendTrackingMixin from '@/mixins/BackendTracking.vue';
import ImpressionTrackingMixin from '@/mixins/ImpressionTrackingMixin.vue';
import TitleWatchlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleWatchlistMutationMixin.vue';

const MarkAsSeenModalGraphql = () => import('@/components/mark-as-seen/MarkAsSeenModalContainerGraphql.vue');
const TitleScoreList = () => import('@/components/TitleScoreList.vue');

import WatchNowButton from '@/components/buttons/WatchNowButton.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import ImpressionPixel from '@/components/sponsored-recommendations/ImpressionPixel.vue';
import RecommendationAdPill from '@/components/sponsored-recommendations/RecommendationAdPill.vue';

import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';

import { ExternalTracker, ExternalTrackerType, ObjectType, SraEventAction } from '@/@types/graphql-types';
import { ModalHelper } from '@/helpers/modal-helper';
import type { MarkAsSeenModalProps } from '@/components/mark-as-seen/MarkAsSeen';
import { MarkAsSeenModalOptions } from '@/components/mark-as-seen/MarkAsSeen';

import { ImpressionTrackingEvents } from '@/enums/events';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import type { ListMutationTitleDetailParam } from '@/helpers/providers/title-actions-provider';
import type { SponsoredAdFragmentFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { SRClickTitleEvent } from '@/components/sponsored-recommendations/sponsored-recommendation-types';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

const IdStateMixin = IdState({
	idProp: (vm: SponsoredRecommendationVideo) =>
		vm.campaignNode.id ? 'LargeNativeAdPoster' + vm.campaignNode.id : 'Poster-NOID',
});

const Language = namespace('language');
const TitleDetailsStore = namespace('titleDetails');

@Component<SponsoredRecommendationVideo>({
	name: 'SponsoredRecommendationVideo',
	components: {
		ImpressionPixel,
		ProviderIcon,
		RecommendationAdPill,
		TitleScoreList,
		WatchNowButton,
	},
	// virtual scroller persistent state
	idState() {
		return {
			imageLoaded: false,
		};
	},
})
export default class SponsoredRecommendationVideo extends Mixins(
	BackendTrackingMixin,
	ImpressionTrackingMixin,
	IdStateMixin,
	TitleWatchlistMutationMixin
) {
	impressionPixelsInternal: Array<string> = [];
	impressionPixelsExternal: Array<string> = [];
	isFading = false;
	isMuted = true;

	@Ref('videoTag') readonly videoTag!: HTMLVideoElement;

	@Prop({ default: () => [] }) declare additionalContexts: (SnowplowModuleContext | SnowplowContext)[];
	@Prop({ required: true }) declare sponsoredAd: SponsoredAdFragmentFragment;
	@Prop({ default: true }) lazy: boolean;
	@Prop({ default: () => [] }) supportedObjectTypes: ObjectType[];

	@Language.Getter declare language: string;

	@TitleDetailsStore.Action setSponsoredRecommendation: (sponsoredAd: SponsoredAdFragmentFragment) => void;

	@Watch('isLoggedIn', { immediate: true })
	onIsLoggedInChange() {
		const { id, details } = this.loginStateTitleDetails;
		if (this.isLoggedIn()) {
			this.mixin_onIsLoggedInChange(
				id,
				details,
				this.isInWatchlist,
				ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS
			);
		}
	}

	/** IMPRESSION TRACKING */
	get observable() {
		return this.getObservableOptions(this.campaignNode.id);
	}
	/** IMPRESSION TRACKING */

	get loginStateTitleDetails() {
		return {
			id: this.campaignNode.id,
			details: {
				title: this.campaignNode.content.title,
				objectType: this.campaignNode.objectType,
				objectId: this.campaignNode.objectId,
			},
		};
	}

	get isInWatchlist() {
		return !!this.campaignNode?.watchlistEntryV2?.createdAt;
	}

	get promotionalImageUrl() {
		return this.sponsoredAd.campaign.promotionalImageUrl;
	}

	get posterKey() {
		return this.promotionalImageUrl || this.campaignNode.objectId;
	}

	get campaignNode() {
		return this.sponsoredAd.campaign!.node as AssertByTypename<
			SponsoredAdFragmentFragment['campaign']['node'],
			'Movie' | 'Show'
		>;
	}

	get volumeIcon() {
		const filename = this.isMuted ? 'volume-xmark' : 'volume-high';
		return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/${filename}.svg`;
	}

	get watchNowLabel() {
		return this.sponsoredAd.campaign.watchNowLabel;
	}

	get impressionTagInternal() {
		return '';
	}

	get impressionTagExternal() {
		return '';
	}

	get providerId() {
		return this.sponsoredAd.campaign.watchNowOffer.package.packageId;
	}

	get seenEpisodeCount() {
		return 'seenState' in this.campaignNode ? this.campaignNode.seenState?.seenEpisodeCount : null;
	}

	get watchNowOffer() {
		return this.sponsoredAd.campaign.watchNowOffer;
	}

	get titleContext() {
		return new SnowplowTitleContextGraphql(this.campaignNode.objectId, this.campaignNode.objectType);
	}

	get clickoutContext() {
		return SnowplowClickoutContextGraphql.fromProviderOffer(this.watchNowOffer);
	}

	get watchNowUctLink() {
		const clickoutUrl = new ClickoutUrl(this.watchNowOffer).set('uct_country', this.country).set('uct_tryout', 1);
		return clickoutUrl.toString();
	}

	mounted() {
		this.injectExternalImpressionHtml();
	}

	// needed by ImpressionTrackingMixin
	impressionCallback() {
		this.$backendTracking.trackSraEvent(SraEventAction.Impression, this.sponsoredAd);

		// External tracking
		this.externalTrackingImpressionUrl();

		if (this.impressionTagInternal) this.addImpressionTrackingPixelInternal();
		if (this.impressionTagExternal) this.addImpressionTrackingPixelExternal();
	}

	onVisibilityChanged(isVisible: boolean) {
		if (isVisible) {
			this.videoTag.play();
		} else {
			this.videoTag.pause();
		}
	}

	watchNowClicked() {
		this.$backendTracking.trackSraEvent(SraEventAction.Click, this.sponsoredAd);
		this.$backendTracking.trackSraEvent(SraEventAction.WatchnowClicked, this.sponsoredAd);

		// External tracking
		this.externalTrackingClickoutUrl();
	}

	// External tracking
	get externalClickoutUrl(): ExternalTracker | undefined {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ClickoutUrl
		);
	}

	externalTrackingClickoutUrl() {
		if (this.externalClickoutUrl) {
			const url = this.externalClickoutUrl.data;
			try {
				fetch(url, {
					mode: 'no-cors',
				});
			} catch (err: any) {}
		}
	}

	get externalImpressionUrl(): ExternalTracker | undefined {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ImpressionUrl
		);
	}

	get externalImpressionHtml(): ExternalTracker | undefined {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ImpressionHtml
		);
	}

	get trailerUrl() {
		return this.sponsoredAd.campaign.promotionalVideo?.url;
	}

	async toggleWatchlist(title: typeof this.campaignNode) {
		this.$backendTracking.trackSraEvent(SraEventAction.WatchlistClicked, this.sponsoredAd);

		const titleDetails: ListMutationTitleDetailParam = {
			title: title.content.title,
			objectType: title.objectType,
			objectId: title.objectId,
		};
		const trackingPayload = {
			source: 'sponsored-recommendations',
		};
		const updatedTitle = await this.mixin_setInWatchlist(
			title.id,
			this.isInWatchlist,
			titleDetails,
			trackingPayload
		);
		if (updatedTitle) {
			const isInWatchlist = !!updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt;
			if (
				isInWatchlist &&
				(title.objectType === ObjectType.Show || title.objectType === ObjectType.ShowSeason) &&
				title.content.isReleased
			) {
				ModalHelper.openModal(
					MarkAsSeenModalGraphql,
					{
						titleId: title.id,
						titleName: title.content.title,
						titleObjectId: title.objectId,
						titleObjectType: title.objectType,
						titleContentType: undefined,
						posterUrl: title.content.posterUrl,
						backdrops: title.content.backdrops,
						seenEpisodeCount: this.seenEpisodeCount,
					} as MarkAsSeenModalProps,
					MarkAsSeenModalOptions,
					'',
					false,
					this.additionalContexts
				);
			}
		}
	}

	onPosterClick() {
		this.setSponsoredRecommendation(this.sponsoredAd);

		this.$backendTracking.trackSraEvent(SraEventAction.Click, this.sponsoredAd);
		this.$backendTracking.trackSraEvent(SraEventAction.Clickout, this.sponsoredAd);

		// make sure the video is muted again, so it doesn't continue to play in the background
		this.isMuted = true;

		// pass down poster-clicked event as 'click'
		const clickTitleEvent: SRClickTitleEvent = { title: this.campaignNode, index: 0 };
		this.$emit('click', clickTitleEvent);
	}

	toggleMute() {
		this.isMuted = !this.isMuted;
		this.videoTag.muted = this.isMuted;

		// legacy event as long as there doesnt exist a SraEventAction.ToggleMute.
		TrackingHelper.trackEvent(
			this.impressionTrackingCategory,
			{
				action: 'toggle_mute',
				value: this.isMuted ? 1 : 0,
			},
			[this.titleContext]
		);
	}

	onMoreInfoClick() {
		this.setSponsoredRecommendation(this.sponsoredAd);

		this.$backendTracking.trackSraEvent(SraEventAction.MoreInfoClicked, this.sponsoredAd);

		// navigate to title detail page
		const clickTitleEvent: SRClickTitleEvent = { title: this.campaignNode, index: 0 };
		this.$emit('navigateTitle', clickTitleEvent);
	}

	onVideoStateChanged(eventName: string, evt: any) {
		if (eventName === 'play') {
			setTimeout(() => {
				this.isFading = true;
			}, 1500);
		}
	}

	onMouseEnter() {
		this.isFading = false;
	}

	onMouseLeave() {
		this.isFading = true;
	}

	private injectExternalImpressionHtml() {
		if (this.externalImpressionHtml?.data) {
			const fragment = document.createRange().createContextualFragment(this.externalImpressionHtml?.data);
			document.head.appendChild(fragment);
		}
	}

	private addImpressionTrackingPixelInternal() {
		this.impressionPixelsInternal.push('_');
	}

	private addImpressionTrackingPixelExternal() {
		this.impressionPixelsExternal.push('_');
	}

	private externalTrackingImpressionUrl() {
		if (this.externalImpressionUrl) {
			const url = this.externalImpressionUrl.data;
			try {
				fetch(url, {
					mode: 'no-cors',
				});
			} catch (err: any) {}
		}
	}
}
